import * as Sentry from "@sentry/react";
import { NestRequestError } from "@/services/nestRequest";

/**
 * RETIRE-WHEN (Great Migration, Track-F FE side): this whole file is the
 * END-STATE MARKER. It shrinks with every fallback disarm and is deleted
 * outright when the last `reportNestFallback*` call site in
 * `src/repositories/` is gone — i.e. when no repository has a Supabase path
 * left to fall back to. Probe:
 *   `grep -rl reportNestFallback src/repositories | grep -v nestFallback`
 *
 * Disarm policy (when may an armed fallback be removed?):
 *   1. its domain's SELECT policy is retired — asserted ABSENT by
 *      supabase/db-tests/tests/005_two_layer_access_model.sh AND confirmed by
 *      probing `pg_policies` on the rig (a policy can be REPLACED under a new
 *      name; `tasks` / `service_requests` kept working that way); OR
 *   2. the repo's `nest-fallback: <repo>.*` Sentry event count is 0 over a
 *      14-day prod window AND the M7 tenancy interceptor is enforcing.
 */

/**
 * Telemetry for the Supabase→NestJS migration's Nest-first / Supabase-fallback
 * pattern (see `src/repositories/*`). A flipped repository method calls its guarded
 * NestJS endpoint first and falls back to Supabase on error.
 *
 * Without telemetry that fallback is invisible: a silently-broken Nest endpoint
 * leaves the app green while 100% of traffic quietly serves from Supabase, so the
 * "flip" becomes unprovable (the #1699 silent-fallback lesson). `reportNestFallback`
 * makes every fallback an alertable Sentry signal, tagged by `repo`/`method` so a
 * single misbehaving endpoint is pinpointable.
 *
 * **Loud-fallback policy (we are moving OFF Supabase — Nest issues must surface):**
 * the error reaching here has already survived `nestRequest`'s timeout + retries,
 * so it is a *genuine* failure, not a transient blip. Infrastructure degradation
 * (network / timeout / 5xx) is reported at **error** level — that is the signal we
 * want to act on. A 4xx means Nest *responded* (alive, just rejected the request),
 * so it stays **warning** to avoid drowning the real "Nest is down" alerts.
 *
 * `scope` is the existing "<repo>.<method>" log tag, e.g. "invoices.list".
 */

function splitScope(scope: string): { repo: string; method: string } {
  const dot = scope.indexOf(".");
  return dot === -1
    ? { repo: scope, method: "" }
    : { repo: scope.slice(0, dot), method: scope.slice(dot + 1) };
}

/** error for genuine Nest degradation; warning when Nest responded (4xx) or kind unknown. */
function levelFor(err: unknown): "error" | "warning" {
  if (err instanceof NestRequestError) {
    if (err.kind === "network" || err.kind === "timeout") return "error";
    if (err.kind === "http" && err.status !== undefined && err.status >= 500) return "error";
    return "warning"; // http 4xx, auth, config — Nest answered or the call was misconfigured
  }
  return "warning"; // non-typed error — be conservative
}

/**
 * React Query fires AbortController signals on unmount to cancel in-flight
 * requests. The resulting AbortError (mapped to kind="timeout" in nestRequest)
 * is not a real Nest failure — it's expected teardown noise. Skip it so it
 * doesn't pollute the fallback signal/noise ratio in Sentry.
 */
function isAbortedRequest(err: unknown): boolean {
  if (err instanceof NestRequestError && err.kind === "timeout") {
    const msg = err.message.toLowerCase();
    return msg.includes("aborterror") || msg.includes("signal is aborted");
  }
  if (err instanceof Error) {
    return err.name === "AbortError" || err.message.toLowerCase().includes("signal is aborted");
  }
  return false;
}

/**
 * The RowScopeGuard endpoints require x-active-role. On app load, React Query
 * fires before the auth context has written the role to sessionStorage — the
 * request reaches Nest without the header and gets a 403 "Missing x-active-role
 * header" (now short-circuited client-side in `nestRequest.ts` before any fetch —
 * see issue #3278). This is an expected startup race: the Supabase fallback
 * handles the load and subsequent calls succeed once the role resolves. Skip
 * Sentry so ~40 dashboard-load events per day don't drown genuine auth failures
 * (Sentry DALLALI-FRONTEND-AJ, 739 events / 25 users, ongoing since 2026-06-03).
 */
function isRoleNotSetYet(err: unknown): boolean {
  return (
    err instanceof NestRequestError &&
    err.kind === "http" &&
    err.status === 403 &&
    err.message === "Missing x-active-role header"
  );
}

/**
 * A page load is considered "booting" for this many ms after navigation start
 * (`performance.now()` is time-since-`timeOrigin`). Wide enough for a slow
 * device/CI to still be hydrating auth, narrow enough that a session expiry /
 * failed refresh / Supabase outage minutes into a live session — a GENUINE
 * auth failure — falls clearly outside it and is reported normally.
 */
export const BOOT_RACE_WINDOW_MS = 15_000;

/**
 * `performance.now()` of the most recent Supabase auth state transition
 * (SIGNED_IN / SIGNED_OUT / TOKEN_REFRESHED / INITIAL_SESSION), stamped by
 * `markAuthTransition()` from `useAuth`'s `onAuthStateChange` listener.
 * `null` until the first transition fires.
 */
let lastAuthTransitionMs: number | null = null;

/**
 * Stamp an auth state transition. Called from `useAuth`'s single
 * `onAuthStateChange` listener for every event EXCEPT a healthy
 * TOKEN_REFRESHED (routine rotation propagates in ms — stamping it would
 * open a needless window every hour): each stamped transition opens a
 * window in which React Query re-fires enabled queries while the new
 * session/token is still propagating (GoTrue writes the session after the
 * callback runs — see the deadlock note in `Auth.tsx`), so a `kind:"auth"`
 * failure inside it is the same race as the boot one, not a genuine outage.
 * SIGNED_OUT stamps too: the `enabled: !!companyId` guards race the
 * companyId clear, so in-flight queries can still fire just after it.
 */
export function markAuthTransition(): void {
  lastAuthTransitionMs = performance.now();
}

/** Test-only: clear the transition stamp so suites don't depend on file order. */
export function _resetAuthTransitionForTest(): void {
  lastAuthTransitionMs = null;
}

/**
 * One step earlier than `isRoleNotSetYet`: no Supabase session exists yet
 * at all (kind === "auth", thrown by `nestRequest.ts` when `useAuth` finds
 * no access token). Same boot-race class — a query fired before the auth
 * context resolved — just caught before the role would even be readable.
 * The literal DQ event (Sentry `nest_kind=auth`, issue #3278) fires on
 * `/auth` for exactly this reason; it isn't a genuine Nest failure. (PR
 * #3289 shipped a blanket `kind === "auth"` suppressor for the same noise —
 * DALLALI-FRONTEND-DW and siblings; this boot-window version supersedes it.)
 *
 * UNLIKE `isRoleNotSetYet` (an exact message/status/kind match), `kind ===
 * "auth"` alone isn't boot-specific — the same NestRequestError fires any
 * time `supabase.auth.getSession()` returns no session, including well into
 * a live session (expired session, a failed silent refresh, a Supabase auth
 * outage). A blanket `kind === "auth"` suppression would hide that entire
 * class of genuine failures. Narrow it to two windows so only actual races
 * are silenced — everything past them still reports:
 *
 *  1. the boot window (page load → auth context hydrating), and
 *  2. the same-length window after the latest auth TRANSITION — a user who
 *     sat on /auth past the boot window and then signed in races the token
 *     propagation exactly like boot does (Sentry DALLALI-FRONTEND-G6/G8/G9:
 *     `nest_kind=auth` on /auth, minutes into the page's life, all during
 *     login).
 */
function isNoSessionYet(err: unknown): boolean {
  if (!(err instanceof NestRequestError) || err.kind !== "auth") return false;
  const now = performance.now();
  if (now < BOOT_RACE_WINDOW_MS) return true;
  return (
    lastAuthTransitionMs !== null &&
    now - lastAuthTransitionMs < BOOT_RACE_WINDOW_MS
  );
}

/**
 * A deterministic 404 from a row-scoped Nest read is Nest's REAL answer —
 * the row is genuinely absent, soft-deleted, cross-tenant, or outside the
 * caller's row scope — not a degradation of the Nest path. Falling back to
 * Supabase re-asks the same question with a LOOSER filter (company_id only,
 * no owner/employee row-scope, no REGA-license check) and burns an extra
 * round-trip to produce the same "not found" — plus a `nest-fallback` noise
 * event per legitimate miss (#3394 / #3395 and the sibling
 * `nest-fallback: *.getById` groups).
 *
 * Call sites should treat this as terminal: return the definitive empty
 * answer directly, with no fallback and no report. This is the central
 * version of the guard `owners.create` and `listings.getPublicById` each
 * hand-rolled inline — new flipped reads kept forgetting it because it
 * wasn't importable.
 *
 * Deliberately NOT folded into `reportNestFallback` itself, and ONLY for
 * call sites whose endpoint 404s deterministically at the APPLICATION level
 * (a NotFoundException on absent/out-of-scope rows — `GET /:id` reads). On
 * a query-parameterised list route (e.g. `GET /listings?unit_id=`) the app
 * never throws 404, so a 404 there can only mean "route missing after a bad
 * deploy" — exactly the regression the loud fallback exists to catch. Do
 * not apply this guard to those (PR #3397 review).
 */
export function isDefinitiveNotFound(err: unknown): boolean {
  return (
    err instanceof NestRequestError && err.kind === "http" && err.status === 404
  );
}

/**
 * Like `reportNestFallback` but re-throws NestRequestError { kind: "auth" }
 * instead of falling through to the Supabase path.
 *
 * Use this for repository methods whose Supabase fallback reads a
 * `security_invoker = true` view (e.g. `*_with_status`) that only grants
 * SELECT to the `authenticated` role. If the Nest call failed because there
 * is no Supabase session, the fallback would hit the same absent session and
 * get a 42501 "permission denied" from PostgREST — producing a second Sentry
 * event for a single auth-race. Re-throwing lets the QueryCache.onError
 * `isNestAuthError()` gate in App.tsx suppress the error cleanly.
 *
 * (Generalises the inline guard that `employees.ts` `.list()` pioneered —
 * that call site now uses this helper too.)
 */
/**
 * A 422 is the server applying a business rule to a well-formed request
 * (e.g. listings `lst-004`: no ad licence, no going live). It is a final
 * answer: re-issuing the write through the Supabase fallback would route the
 * mutation around the rule, so a write path whose Supabase fallback doesn't
 * enforce that rule rethrows it before falling back (listings create/update).
 */
export function isDomainRejection(err: unknown): boolean {
  return err instanceof NestRequestError && err.kind === "http" && err.status === 422;
}

export function reportNestFallbackOrRethrowAuth(scope: string, err: unknown): void {
  if (err instanceof NestRequestError && err.kind === "auth") throw err;
  reportNestFallback(scope, err);
}

/**
 * Write-path variant of `reportNestFallback` for NON-IDEMPOTENT mutations
 * (money writes, creates). The read-path fallback contract — "any Nest error →
 * serve from Supabase" — is WRONG for a mutation: when the Nest attempt died
 * with the commit state unknown (network drop, timeout, 5xx — #3316's
 * properties.create 500 fired AFTER the insert committed), re-issuing the
 * write through the Supabase fallback double-writes. The `Idempotency-Key`
 * header only protects replays through Nest; the Supabase RPC fallback does
 * not see it.
 *
 * Policy (the second half of the 2026-07-14 friction note, now enforced):
 *   - Nest answered with a 4xx        → it rejected the request without
 *     committing; falling back is safe → report (same suppressors as reads)
 *     and return, caller proceeds to Supabase. EXCEPT 429: no commit happened,
 *     but the server deliberately refused the write — falling back would route
 *     the mutation AROUND the rate limit through the un-throttled Supabase
 *     path, so a throttle would silently stop throttling (PR #3371 review).
 *   - kind "config" (API_URL unset)   → no request was ever sent → safe.
 *   - EVERYTHING ELSE (network, timeout, 5xx, abort, kind "auth", non-typed
 *     errors such as a response-parse failure after a 2xx) → the write may
 *     have committed (or, for auth, the fallback would fail the same way) →
 *     REPORT and RETHROW. The caller's error path surfaces it to the user,
 *     who retries deterministically.
 *
 * The rethrow is reported under its own fingerprint (`nest-write-blocked`)
 * at error level: every suppressed fallback is a Nest write path in genuine
 * degradation and must stay alertable.
 */
export function reportNestFallbackOrRethrowUnknownCommit(scope: string, err: unknown): void {
  const safeToFallback =
    err instanceof NestRequestError &&
    (err.kind === "config" ||
      (err.kind === "http" &&
        err.status !== undefined &&
        err.status < 500 &&
        err.status !== 429));

  if (safeToFallback) {
    reportNestFallback(scope, err);
    return;
  }

  const { repo, method } = splitScope(scope);
  const nre = err instanceof NestRequestError ? err : undefined;

  console.warn(
    `[${scope}] Nest write failed with unknown commit state — NOT falling back to Supabase (duplicate-write guard)`,
    err,
  );

  Sentry.captureMessage(`nest-write-blocked: ${scope}`, {
    level: "error",
    fingerprint: ["nest-write-blocked", repo, method],
    tags: {
      nest_fallback: false,
      repo,
      method,
      ...(nre?.kind ? { nest_kind: nre.kind } : {}),
      ...(nre?.status !== undefined ? { nest_status: nre.status } : {}),
    },
    extra: {
      scope,
      error: err instanceof Error ? err.message : String(err),
      note: "Supabase fallback suppressed for a non-idempotent write: the Nest attempt's commit state is unknown (or it was a deliberate 429 refusal), so a fallback re-issue could double-write or bypass a rate limit.",
      ...(nre?.durationMs !== undefined ? { durationMs: nre.durationMs } : {}),
      ...(nre?.requestId ? { requestId: nre.requestId } : {}),
      ...(nre?.url ? { url: nre.url } : {}),
    },
  });

  throw err;
}

/**
 * Write-path variant of `reportNestFallback` for IDEMPOTENT mutations whose
 * endpoint gives DEFINITIVE answers (the a14 companies control-plane writes:
 * set-main / set-active / settings PATCH — re-running them is a no-op).
 *
 * Policy (spec a14 §2.4, the counterpart of
 * `reportNestFallbackOrRethrowUnknownCommit` above):
 *   - Genuine transport failure (kind config/auth/timeout/network, or http
 *     5xx) → the server never gave an answer; the write is idempotent, so
 *     re-issuing through the Supabase path is safe → report + return, caller
 *     proceeds to the fallback.
 *   - A definitive 4xx (403/404/400/429) is the server's ANSWER — rethrow, do
 *     NOT re-run the RPC. A 403 falling back to Supabase would mask a broken
 *     guard and double-attempt a control-plane write; a 429 fallback would
 *     route around the throttle.
 *   - Anything else (e.g. a 2xx body that failed the shared-schema parse) is
 *     also rethrown: the server answered with a malformed shape — a deploy bug
 *     that must fail LOUD, not silently serve from the legacy path.
 */
export function reportNestFallbackOrRethrowDefinitive(
  scope: string,
  err: unknown,
): void {
  const safeToFallback =
    err instanceof NestRequestError &&
    (err.kind === "config" ||
      err.kind === "auth" ||
      err.kind === "timeout" ||
      err.kind === "network" ||
      (err.kind === "http" && err.status !== undefined && err.status >= 500));

  if (safeToFallback) {
    reportNestFallback(scope, err);
    return;
  }
  throw err;
}

export function reportNestFallback(scope: string, err: unknown): void {
  if (isAbortedRequest(err)) return;
  if (isRoleNotSetYet(err)) return;
  if (isNoSessionYet(err)) return;

  console.warn(`[${scope}] Nest path failed — falling back to Supabase`, err);

  const { repo, method } = splitScope(scope);
  const level = levelFor(err);

  // Rich context off the typed transport error (undefined for legacy/plain errors).
  const nre = err instanceof NestRequestError ? err : undefined;

  Sentry.addBreadcrumb({
    category: "nest.fallback",
    level,
    message: scope,
  });

  // The scope is IN the message (not just the tags): Sentry's alert→GitHub
  // automation titles the auto-filed issue with the message. The fingerprint
  // version prefix forces NEW Sentry issue groups so the title is taken from
  // the *current* message format "nest-fallback: {scope}" instead of the
  // old bare "nest-fallback" title that was set when those groups were first
  // created (#3307, #3296, #3286, #3326, …). Grouping granularity
  // (one Sentry issue per endpoint) is unchanged; old groups stop receiving
  // events and auto-resolve. See nestFallback.ts for full context.
  Sentry.captureMessage(`nest-fallback: ${scope}`, {
    level,
    fingerprint: ["nest-fallback-v2", repo, method],
    tags: {
      nest_fallback: true,
      repo,
      method,
      // New, queryable facets — alert on `nest_kind:network OR nest_kind:timeout`.
      ...(nre?.kind ? { nest_kind: nre.kind } : {}),
      ...(nre?.status !== undefined ? { nest_status: nre.status } : {}),
    },
    extra: {
      scope,
      error: err instanceof Error ? err.message : String(err),
      ...(nre?.durationMs !== undefined ? { durationMs: nre.durationMs } : {}),
      ...(nre?.url ? { url: nre.url } : {}),
    },
  });
}
